import { useId, useRef, useState } from 'react'
import { useDismiss } from '@/hooks/useDismiss'
import { useIsPhone } from '@/hooks/useIsPhone'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { SheetHandle, SheetWrapper, sheetDocked, sheetSafeArea } from '@/components/ui/Sheet'
import { focusRing } from '@/components/ui/buttonStyles'
import { cn } from '@/utils/cn'

interface GrammarHintProps {
  /** What the learner reads in the table: a pronoun or a case name. */
  label: string
  /** The plain-language gloss, e.g. the English pronoun or "subject form". */
  title: string
  /** The formal name of the slot, spelled out. */
  description?: string
  className?: string
}

/**
 * A grammatical label that explains itself when tapped. The table stays keyed by
 * everyday words — "ég", "Nominative" — and the terminology waits in a small popover
 * (a bottom sheet on a phone) for whoever wants it.
 */
export function GrammarHint({ label, title, description, className }: GrammarHintProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const isPhone = useIsPhone()
  const titleId = useId()

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  useDismiss(wrapperRef, close, open, { alsoInside: sheetRef })
  useSheetAnchor(open, triggerRef, sheetRef)

  return (
    <span ref={wrapperRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          '-mx-1.5 min-h-11 rounded px-1.5 text-left text-sm text-muted transition-colors duration-200',
          'underline decoration-dotted decoration-muted/40 underline-offset-4',
          'hover:text-heading',
          open && 'text-heading',
          focusRing,
        )}
      >
        {label}
      </button>

      {open && (
        <SheetWrapper isPhone={isPhone} onScrimClick={close}>
          <div
            role="dialog"
            aria-labelledby={titleId}
            ref={sheetRef}
            data-testid={isPhone ? 'bottom-sheet' : undefined}
            className={cn(
              'animate-fade-in',
              isPhone
                ? cn(sheetDocked, sheetSafeArea, 'px-5 pb-5')
                : 'absolute left-0 top-full z-50 mt-1 w-max max-w-xs rounded-lg border border-border-divider bg-card px-4 py-3 shadow-lg',
            )}
          >
            <SheetHandle />
            <p id={titleId} className="font-serif text-base text-heading">
              <span className="text-muted">{label}</span>
              <span aria-hidden="true" className="mx-2 text-muted/60">·</span>
              {title}
            </p>
            {description && (
              <p className="mt-1 font-mono text-xs uppercase tracking-wider text-muted">{description}</p>
            )}
          </div>
        </SheetWrapper>
      )}
    </span>
  )
}
